import { apiClient, VerifiedField } from '../../api/client';
import type { components } from '../../api/generated/schema';
import type { Contact, PasswordChange } from '../../domain/account';
import { mapped } from '../errors';
import type { AccountRepository } from './AccountRepository';
import {
  toCancelChangeResult,
  toContactMatch,
  toContactPoint,
  toMe,
  toPasswordChange,
  toPendingChange,
  toSocialConnection,
} from './mapping';

type S = components['schemas'];

/** Email and phone changes are the same algorithm, parameterized by field. */
function verified(field: VerifiedField) {
  return {
    start: (value: string) =>
      mapped(async () => toPendingChange(await apiClient.requestContactChange(field, value))),
    confirm: (code: string) =>
      mapped(async () => toMe(await apiClient.confirmContactChange(field, code))),
    resend: () => mapped(async () => toPendingChange(await apiClient.resendContactCode(field))),
    cancel: () =>
      mapped(async () => toCancelChangeResult(await apiClient.cancelContactChange(field))),
  };
}

const email = verified('email');
const phone = verified('phone');

export const httpAccountRepository: AccountRepository = {
  me: () => mapped(async () => toMe(await apiClient.accountProfile())),
  updateName: (firstName: string, lastName: string) =>
    mapped(async () =>
      toMe(await apiClient.updateAccountProfile({ first_name: firstName, last_name: lastName })),
    ),
  uploadAvatar: (image: File) =>
    mapped(async () => (await apiClient.uploadAvatar(image)).avatar_url),
  removeAvatar: () => mapped(() => apiClient.deleteAvatar()),

  startEmailChange: email.start,
  confirmEmailChange: email.confirm,
  resendEmailCode: email.resend,
  cancelEmailChange: email.cancel,

  startPhoneChange: phone.start,
  confirmPhoneChange: phone.confirm,
  resendPhoneCode: phone.resend,
  cancelPhoneChange: phone.cancel,

  changePassword: (change: PasswordChange) =>
    mapped(async () => {
      await apiClient.setPassword(toPasswordChange(change));
    }),
  socialConnections: () =>
    mapped(async () => (await apiClient.socialConnections()).map(toSocialConnection)),

  contactPoints: () =>
    mapped(async () =>
      (await apiClient.get<S['ContactPoint'][]>('/api/v1/account/contact-points/')).map(
        toContactPoint,
      ),
    ),
  updateContactPoint: (id: string, discoverable: boolean) =>
    mapped(async () =>
      toContactPoint(
        await apiClient.send<S['ContactPoint']>(
          'PATCH',
          `/api/v1/account/contact-points/${encodeURIComponent(id)}/`,
          { discoverable },
        ),
      ),
    ),
  removeContactPoint: (id: string) =>
    mapped(async () => {
      await apiClient.send('DELETE', `/api/v1/account/contact-points/${encodeURIComponent(id)}/`);
    }),
  matchContacts: (contacts: Contact[]) =>
    mapped(async () =>
      (
        await apiClient.send<S['ContactMatch'][]>('POST', '/api/v1/contacts/match/', { contacts })
      ).map(toContactMatch),
    ),
  deleteAccount: () =>
    mapped(async () => {
      await apiClient.send('DELETE', '/api/v1/account/');
    }),
};
